import { useApolloClient } from '@apollo/client';
import { Form, message } from 'antd';
import { Formik } from 'formik';
import { pick } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type UserGroupSpecsFragment } from '../../../api/fragments/UserGroupSpecs';
import {
    UpdateUserGroupDocument,
    type UpdateUserGroupMutation,
    type UpdateUserGroupMutationVariables,
} from '../../../api/mutations/updateUserGroup';
import { useListAvailableUserGroupsQuery } from '../../../api/queries/listAvailableUserGroups';
import { type UserGroupSettings } from '../../../api/types';
import InputField from '../../../components/fields/InputField';
import SwitchField from '../../../components/fields/SwitchField';
import useHandleError from '../../../utilities/useHandleError';
import useSystemSwitchData from '../../../utilities/useSystemSwitchData';
import SelectUserGroupsWithSearchField from './SelectUserGroupsWithSearchField';

export type UserGroupUpdateFormProps = {
    userGroup: UserGroupSpecsFragment;
    onComplete: () => void;
};

type FormValues = UserGroupSettings;

const UserGroupUpdateForm = ({ userGroup, onComplete }: UserGroupUpdateFormProps) => {
    const { t } = useTranslation(['userGroupDetails']);
    const { yesNoSwitch } = useSystemSwitchData();
    const apolloClient = useApolloClient();

    const userGroupId = userGroup.id;
    const { data } = useListAvailableUserGroupsQuery({
        fetchPolicy: 'cache-and-network',
    });
    const dataSource = useMemo(() => {
        const userGroups = data?.page.items || [];

        // ensure that options do not include the user group that is being updated and it is from same company
        return userGroups.filter(group => group.id !== userGroup.id && group.company.id === userGroup.company.id);
    }, [data, userGroup]);

    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('userGroupDetails:updateDrawer.messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<UpdateUserGroupMutation, UpdateUserGroupMutationVariables>({
                    mutation: UpdateUserGroupDocument,
                    variables: { userGroupId, settings: values },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('userGroupDetails:updateDrawer.messages.updateSuccessful'),
                key: 'primary',
            });

            onComplete();
        },
        [onComplete, apolloClient, userGroupId, t]
    );

    const initialValues = useMemo(
        (): FormValues => ({
            ...pick(['displayName', 'description', 'isParallel', 'superiorUserGroupIds', 'isActive'], userGroup),
        }),
        [userGroup]
    );

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit}>
            {({ handleSubmit }) => (
                <Form id="updateUserGroup" layout="vertical" name="updateUserGroup" onSubmitCapture={handleSubmit}>
                    <InputField
                        {...t('userGroupDetails:updateDrawer.displayName', { returnObjects: true })}
                        name="displayName"
                    />
                    <InputField
                        {...t('userGroupDetails:updateDrawer.description', { returnObjects: true })}
                        name="description"
                    />
                    <SwitchField
                        {...t('userGroupDetails:updateDrawer.isParallel', { returnObjects: true })}
                        name="isParallel"
                        {...yesNoSwitch}
                        required
                    />
                    <SwitchField
                        {...t('userGroupDetails:updateDrawer.isActive', { returnObjects: true })}
                        name="isActive"
                        {...yesNoSwitch}
                    />
                    <SelectUserGroupsWithSearchField
                        {...t('userGroupDetails:updateDrawer.addUserGroups', { returnObjects: true })}
                        mode="multiple"
                        name="superiorUserGroupIds"
                        style={{ width: '100%' }}
                        userGroupId={userGroupId}
                        userGroups={dataSource}
                    />
                </Form>
            )}
        </Formik>
    );
};

export default UserGroupUpdateForm;
